/**
 * Card shell for one chart block: a coloured dot, an uppercase title, an optional
 * sub-line, an optional right-hand slot (tabs), and the mandatory
 * « Graphique » / « Tableau » twin-view toggle when a `table` node is supplied. The
 * table view is the a11y fallback for the SVG, which is why every chart ships one.
 *
 * Style (mock `recommandations.html` → `.card` / `.card-head` / `.seg`, 2026-10-07):
 * 20px radius, 24/28 padding, 1px `VIZ.border`, NO shadow — the hairline alone
 * separates the card from the page, and a shadow under a 20px radius reads as a
 * modal. Title is 13px/600 uppercase at .09em in label ink, preceded by an 8px dot
 * whose colour groups the card into a family (violet = score, green = rate /
 * channel, orange = segmentation, ink = neutral).
 *
 * The view toggle is ICON-ONLY: two 30×26 pills in a `#F1F1F4` track, active one
 * white with a violet glyph. `chartLabel` / `tableLabel` are still required — they
 * become the `title` + `aria-label`, so the control keeps a name in every language
 * even though it shows none.
 *
 * Every string arrives as a prop (the two toggle labels included) so the container
 * supplies i18n'd copy and this file stays free of `useTranslation`.
 */

import { useState, type ReactNode } from 'react';
import { Box, Paper, ToggleButton, ToggleButtonGroup, Tooltip, Typography } from '@mui/material';
import { CARD, VIZ } from './tokens';

/** 15px stroked glyphs, copied from the mock's inline SVG so the weights match. */
function ChartGlyph() {
  return (
    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" aria-hidden="true">
      <path d="M4 20V10M10 20V4M16 20v-7M22 20H2" />
    </svg>
  );
}

function TableGlyph() {
  return (
    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" aria-hidden="true">
      <rect x="3" y="4" width="18" height="16" rx="2" />
      <path d="M3 10h18M3 15h18M9 10v10" />
    </svg>
  );
}

/** The mock's `.seg` — a 2px-padded track of 30×26 icon pills. */
const VIEW_SEG_SX = {
  bgcolor: '#F1F1F4',
  borderRadius: '8px',
  p: '2px',
  gap: '2px',
  flex: 'none',
  '& .MuiToggleButton-root': {
    border: 0,
    borderRadius: '6px !important',
    width: 30,
    height: 26,
    minHeight: 26,
    p: 0,
    color: '#8A8A94',
    '&:hover': { color: VIZ.ink, bgcolor: 'transparent' },
  },
  '& .MuiToggleButton-root.Mui-selected': {
    bgcolor: '#fff',
    color: VIZ.violet,
    boxShadow: '0 1px 2px rgba(20,20,24,.12)',
    '&:hover': { bgcolor: '#fff', color: VIZ.violet },
  },
};

export interface ChartCardProps {
  title: string;
  /** Family colour for the leading dot. Defaults to the brand violet. */
  dot?: string;
  hint?: ReactNode;
  right?: ReactNode;
  table?: ReactNode;
  /** Names for the twin-view toggle — shown as tooltips, read as the a11y label. */
  chartLabel?: string;
  tableLabel?: string;
  children: ReactNode;
  sx?: object;
}

export default function ChartCard({
  title,
  dot = VIZ.violet,
  hint,
  right,
  table,
  chartLabel = 'Graphique',
  tableLabel = 'Tableau',
  children,
  sx,
}: ChartCardProps) {
  const [view, setView] = useState<'chart' | 'table'>('chart');

  return (
    <Paper
      variant="outlined"
      sx={{
        p: `${CARD.padY * 8}px ${CARD.padX * 8}px`,
        borderRadius: `${CARD.radius}px`,
        border: `1px solid ${VIZ.border}`,
        boxShadow: 'none',
        display: 'flex',
        flexDirection: 'column',
        ...sx,
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 1.5, flexWrap: 'wrap' }}>
        <Box sx={{ flex: '1 1 300px', minWidth: 0 }}>
          <Typography
            component="h3"
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              m: 0,
              fontSize: 13,
              fontWeight: 600,
              letterSpacing: '0.09em',
              textTransform: 'uppercase',
              color: VIZ.ink2,
            }}
          >
            <Box component="span" sx={{ width: 8, height: 8, borderRadius: '50%', flex: 'none', bgcolor: dot }} />
            {title}
          </Typography>
          {/* Under the title, full card width — beside the right-hand slot (up to 7
              category tabs plus the view toggle) it only got the header's first
              column, so a two-clause sentence grew the header by three lines. */}
          {hint && (
            <Typography sx={{ mt: 0.75, fontSize: 13.5, lineHeight: 1.45, color: VIZ.muted }}>
              {hint}
            </Typography>
          )}
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flex: 'none' }}>
          {right}
          {table && (
            <ToggleButtonGroup
              size="small"
              exclusive
              value={view}
              onChange={(_, v) => v && setView(v)}
              sx={VIEW_SEG_SX}
            >
              <ToggleButton value="chart" aria-label={chartLabel}>
                <Tooltip title={chartLabel}>
                  <Box sx={{ display: 'flex' }}>
                    <ChartGlyph />
                  </Box>
                </Tooltip>
              </ToggleButton>
              <ToggleButton value="table" aria-label={tableLabel}>
                <Tooltip title={tableLabel}>
                  <Box sx={{ display: 'flex' }}>
                    <TableGlyph />
                  </Box>
                </Tooltip>
              </ToggleButton>
            </ToggleButtonGroup>
          )}
        </Box>
      </Box>
      <Box sx={{ mt: CARD.gap }}>{table && view === 'table' ? table : children}</Box>
    </Paper>
  );
}

/**
 * Legend — mandatory from 2 series, absent for a single one. Identity comes from the
 * coloured swatch; the TEXT stays in the ink tokens (never the series colour).
 */
export function VizLegend({
  items,
  kind = 'rect',
}: {
  items: { label: string; color: string }[];
  kind?: 'rect' | 'line';
}) {
  return (
    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2.5, mt: 1.5 }}>
      {items.map((it) => (
        <Box
          key={it.label}
          sx={{ display: 'inline-flex', alignItems: 'center', gap: 1, fontSize: 13, color: VIZ.ink2 }}
        >
          <Box
            sx={{
              flex: 'none',
              width: kind === 'line' ? 16 : 10,
              height: kind === 'line' ? 3 : 10,
              borderRadius: kind === 'line' ? '2px' : '3px',
              bgcolor: it.color,
              // A pale swatch needs its own edge or it vanishes on white.
              border: it.color === VIZ.neutral ? '1px solid #D8D5E0' : undefined,
            }}
          />
          <span>{it.label}</span>
        </Box>
      ))}
    </Box>
  );
}

/** "No data" line — a chart with nothing to draw says so, it does not draw zeros. */
export function EmptyNote({ children }: { children: ReactNode }) {
  return (
    <Typography sx={{ color: VIZ.muted, fontSize: 13.5, py: 1.5 }}>{children}</Typography>
  );
}

/**
 * The mock's `.empty` state — a dashed panel with an icon, for a card whose data
 * exists but whose SEGMENTATION is empty ("none of the cited targets carries a
 * tag"). Distinct from `EmptyNote`, which is a bare line for "nothing measured".
 */
export function EmptyPanel({ children, icon }: { children: ReactNode; icon?: ReactNode }) {
  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 1.75,
        p: 2.25,
        border: '1px dashed #E2E2E7',
        borderRadius: `${CARD.tileRadius}px`,
        bgcolor: '#FCFCFD',
      }}
    >
      {icon && (
        <Box
          sx={{
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: 36,
            height: 36,
            borderRadius: '50%',
            bgcolor: '#FCEDE5',
            color: VIZ.serious,
            flex: 'none',
          }}
        >
          {icon}
        </Box>
      )}
      <Typography sx={{ m: 0, fontSize: 13.5, lineHeight: 1.45, color: VIZ.muted }}>
        {children}
      </Typography>
    </Box>
  );
}
